import type { LicenceDto, PaginatedListOfLicenceDto } from "../../models/models"
import type { PagingActions, PagingOptions } from "../../models/types"
import { createLicence, deleteLicence, getAllLicences, getLicence, updateLicence } from "../../services/LicenceService"
import { getQueryString } from "../../utils"
import type { AppState } from "../app.state"
import type { AppThunkAction, AppThunkDispatch } from "../app.types"
import { history } from "./../Store"
import type { Licence, LicenceAction, LicenceListAction } from "./licence.types"

export const LICENCE_FETCH: string = "LICENCE_FETCH"
export const LICENCE_FETCH_ALL: string = "LICENCE_FETCH_ALL"
export const LICENCE_LOAD_LIST: string = "LICENCE_LOAD_LIST"

export const LICENCE_DELETE: string = "LICENCE_DELETE"

export const LICENCE_CREATE: string = "LICENCE_CREATE"

export const LICENCE_UPDATE: string = "LICENCE_UPDATE"

export const LICENCE_CHANGE_PAGE: string = "LICENCE_CHANGE_PAGE"
export const LICENCE_LOAD_LIST_OPTIONS: string = "LICENCE_LOAD_LIST_OPTIONS"

export const LICENCE_LOAD: string = "LICENCE_LOAD"
export const LICENCE_LOAD_DEFAULT: string = "LICENCE_LOAD_DEFAULT"

export function fetchLicence(id: string): AppThunkAction<void, LicenceAction> {
  return async (dispatch: AppThunkDispatch<LicenceAction>) => {
    dispatch({ type: LICENCE_FETCH })

    const licence: LicenceDto = await getLicence(id)

    dispatch(loadLicence(licence))
  }
}

export function fetchAllLicences(
  pageIndex = 0,
  pageSize = 50
): AppThunkAction<void, LicenceListAction | PagingActions> {
  return async (dispatch: AppThunkDispatch<LicenceListAction | PagingActions>, getState: () => AppState) => {
    dispatch({ type: LICENCE_FETCH_ALL })

    const state: AppState = getState()
    const queryString: string = getQueryString(pageIndex, pageSize, state.contentManager.sort)

    const pagedList: PaginatedListOfLicenceDto = await getAllLicences(queryString)
    const pagingOptions: PagingOptions = {
      currentPage: pagedList.pageIndex,
      totalPages: pagedList.totalPages,
      pageSize: pageSize
    }

    dispatch(loadLicenceList(pagedList.items as LicenceDto[]))
    dispatch({ type: LICENCE_LOAD_LIST_OPTIONS, payload: pagingOptions })
  }
}

export function loadDefaultLicence(): LicenceAction {
  return {
    type: LICENCE_LOAD_DEFAULT
  }
}

export function loadLicence(licence: LicenceDto): LicenceAction {
  return {
    type: LICENCE_LOAD,
    payload: licence
  }
}

export function loadLicenceList(licences: LicenceDto[]): LicenceListAction {
  return {
    type: LICENCE_LOAD_LIST,
    payload: licences
  }
}

export function removeLicence(id: string): AppThunkAction<void, LicenceAction> {
  return async (dispatch: AppThunkDispatch<LicenceAction>) => {
    dispatch({ type: LICENCE_DELETE })
    await deleteLicence(id)
    dispatch(fetchAllLicences())
  }
}

export function createNewLicence(licence: Licence): AppThunkAction<void, LicenceAction> {
  return async (dispatch: AppThunkDispatch<LicenceAction>) => {
    dispatch({ type: LICENCE_CREATE })
    const newId: string = await createLicence(licence)
    const newLicence: LicenceDto = await getLicence(newId)

    history.replace(`/manage/licences/${newId}`)

    dispatch(loadLicence(newLicence))
  }
}

export function updateExistingLicence(licence: Licence): AppThunkAction<void, LicenceAction> {
  return async (dispatch: AppThunkDispatch<LicenceAction>) => {
    dispatch({ type: LICENCE_UPDATE })

    await updateLicence(licence)

    dispatch(fetchAllLicences())
  }
}

export function changeLicencePage(page: number): AppThunkAction<void, PagingActions> {
  return (dispatch: AppThunkDispatch<PagingActions>, getState: () => AppState) => {
    const state = getState()
    dispatch({ type: LICENCE_CHANGE_PAGE, payload: page })

    dispatch(fetchAllLicences(page, state.licences.pagingOptions.pageSize))
  }
}
